/**
 * v0.79.18 — single-source-of-truth permanent auth-failure detection.
 *
 * Two ingress points fire `aq:auth-permanent-failure` CustomEvent on window:
 *   - REST 401/403 from any reconcile/request path (G1)
 *   - WS close.code=1008 (G2)
 *
 * Module-level flag collapses concurrent calls into one — protects against
 * three WS connections + a parallel REST fetch all detecting auth failure
 * within the same tick. main.ts listener also has its own idempotency
 * (window.__authFailureHandled) for defense-in-depth.
 *
 * Closes S1285 architectural class. v0.84.0 — also hosts fetchPlayerState
 * wrapper for the four /player-state ingresses (deepening).
 */

import { API_BASE } from './api-base';

let alreadyDispatched = false;

export function isPermanentAuthFailure(resp: Response): boolean {
  return resp.status === 401 || resp.status === 403;
}

export function dispatchAuthFailure(reason: string): void {
  if (alreadyDispatched) return;
  alreadyDispatched = true;
  window.dispatchEvent(
    new CustomEvent('aq:auth-permanent-failure', { detail: { reason } })
  );
}

/**
 * Resets the module-level flag so the next REST/WS auth failure can fire
 * again. Exposed for tests + called by `armAuthFailureHandler()` below.
 *
 * v0.79.30 (Bug 4) — REMOVED from `clearAllGameData`. Pre-fix, calling this
 * inside the auth-failure handler created a spam loop: handler ran → reset
 * guard → next in-flight 401 (chat-poll, lobby history fetch, etc.) fired
 * dispatchAuthFailure again → handler ran again → toast №2 → guard reset
 * → toast №3 → etc. The reset MUST happen on a FRESH session start (e.g.
 * successful joinByCode), NOT immediately after logout while in-flight
 * stale-token requests are still resolving.
 */
export function resetAuthFailureFlag(): void {
  alreadyDispatched = false;
}

/**
 * v0.79.30 (Bug 4) — Arm the auth-failure handler for a fresh session.
 *
 * Resets BOTH guards (module-level `alreadyDispatched` AND the window-level
 * `__authFailureHandled` set by main.ts handler) so the next 401/403/WS-1008
 * can fire the logout flow.
 *
 * MUST be called from join-success paths, NOT from clearAllGameData. The
 * latter is invoked by the handler itself, and resetting guards mid-handler
 * created the v0.79.30 spam loop (see comment on `resetAuthFailureFlag`).
 *
 * Idempotent — calling on a fresh app launch (where guards were already
 * false) is a no-op.
 */
export function armAuthFailureHandler(): void {
  alreadyDispatched = false;
  if (typeof window !== 'undefined') {
    window.__authFailureHandled = false;
  }
}

/**
 * Bug C5 (2026-05-01) — verify a 401/403 survives a retry before
 * classifying it as permanent. Pre-fix any transient session-refresh
 * race or network blip mid-flight got the player tossed back to /join
 * (clearAllGameData wiped state on F5 with valid persisted token).
 *
 * Semantics:
 *   - non-401/403 → permanent: false, no retry attempted (fast path)
 *   - 401/403 → wait `retryDelayMs`, call `retryFetch()`, classify by result
 *   - retry returns 401/403 → permanent: true
 *   - retry returns anything else → permanent: false, freshResp returned
 *     so caller can use it without re-fetching
 *   - retry throws → permanent: false (treat as transient; let the next
 *     reconcile try again rather than force logout from possibly-offline)
 */
export async function isPermanentAuthFailureWithRetry(
  resp: Response,
  retryFetch: () => Promise<Response>,
  retryDelayMs: number = 1000,
): Promise<{ permanent: boolean; freshResp?: Response }> {
  if (resp.status !== 401 && resp.status !== 403) {
    return { permanent: false };
  }
  if (retryDelayMs > 0) {
    await new Promise((r) => setTimeout(r, retryDelayMs));
  }
  try {
    const fresh = await retryFetch();
    if (fresh.status === 401 || fresh.status === 403) {
      return { permanent: true };
    }
    return { permanent: false, freshResp: fresh };
  } catch {
    return { permanent: false };
  }
}

/** Shape the backend returns for GET /api/games/{code}/player-state.
 *  Centralized here (rather than in poll-player-state.ts) so the
 *  fetchPlayerState wrapper added in Task 2 can return it directly without
 *  a circular import. poll-player-state.ts re-exports for callers that
 *  already imported from there. */
export interface PlayerStateResponse {
  currentLocationIndex: number;
  finishedAt?: number | null;
  penaltyMs?: number;
  startedAt?: number | null;
  /** Session 53 /review #1 — server-stamped time the team entered the
   *  CURRENT location. Preferred over `startedAt` as the timer anchor
   *  so post-org-advance navigation starts at 0:00 elapsed, not at
   *  "1 hour since team first joined". */
  locationEnteredAt?: number | null;
  /** Session 53 /review #1 — server's wall clock when the response was
   *  built. Used by computeLocalAnchor for clock-skew correction. */
  serverNow?: number;
  /** Session 79 lobby reconcile — gameStatus surfaces LOBBY/ACTIVE/FINISHED
   *  so reconcileLobbyAgainstServer can promote out without polling
   *  /player-state through a different shape. Optional to stay back-compat
   *  with older server build. */
  gameStatus?: string;
  nextRiddleKey?: string | null;
  encryptedRiddleText?: string | null;
  encryptedHintText?: string | null;
  /** /review 2026-04-27 v0.79.16 — symmetric with reconcileFromServer's
   *  geo sync (location-view.ts:248-266). Backend exposes the team's
   *  currently-verified geo state (if any) on /player-state so a poll-
   *  driven advance can seed `state.geoVerified` for the new stage,
   *  not just clear the old one. Fixes F4 from the v0.79.13-15
   *  adversarial review.
   *  ADDRESS-only stages broadcast lat/lon=null with a non-empty
   *  address — both shapes count as «verified».
   */
  geoVerifiedLat?: number | null;
  geoVerifiedLon?: number | null;
  geoVerifiedAddress?: string | null;
  /** v0.80.6 — multi-hint schedule for the current location. Mirror of the
   *  same wire field exposed via reconcileFromServer in v0.80.5. The 5-sec
   *  org-advance poll is the SECOND consumer of /player-state and was the
   *  missing leg in v0.80.5 — without it, an online captain whose WS frame
   *  was lost (reconnect race / org-driven advance) gets the new riddle via
   *  poll but no hint schedule. Same merge semantics as reconcile: prefer
   *  server-non-empty, fall back to existing local. */
  encryptedHints?: Array<{ encryptedText: string; delaySec: number; orderIndex: number; type?: string }>;
}

/** Discriminated result of fetchPlayerState. Pattern-match on `kind`:
 *   - 'ok'           → typed PlayerStateResponse data ready to apply
 *   - 'auth-failed'  → wrapper already dispatched aq:auth-permanent-failure;
 *                      caller returns its sentinel and stops processing
 *   - 'network-blip' → transient; caller returns NO_CHANGE and waits for
 *                      next reconcile (cold-mount, visibility, poll tick)
 */
export type PlayerStateFetchResult =
  | { kind: 'ok'; data: PlayerStateResponse }
  | { kind: 'auth-failed'; status: number }
  | { kind: 'network-blip' };

export interface FetchPlayerStateOptions {
  gameCode: string;
  playerId: string;
  playerToken: string;
  /** Telemetry tag — included in dispatchAuthFailure reason as
   *  `${context}-${status}`. Stable strings: 'reconcile-lobby',
   *  'reconcile-location', 'prevkey-recovery', 'poll-player-state'. */
  context: string;
  /** Optional AbortSignal — wired into the FIRST fetch attempt. The
   *  retry call is intentionally signal-less (a fresh attempt, not a
   *  cancellable one). */
  signal?: AbortSignal;
  /** Test injection. Defaults to global fetch. */
  fetchFn?: typeof fetch;
}

/**
 * Wraps the four-step /player-state fetch dance into one call.
 *
 * Steps encapsulated:
 *   1. Build URL with API_BASE + encoded path/query params (single
 *      source of truth — was duplicated 4× across raw call sites).
 *   2. Run the first fetch with the caller's signal. Any throw
 *      (network offline, AbortError from a ReconcileGate timeout) is
 *      caught and surfaces as `network-blip` — caller returns
 *      NO_CHANGE and the gate's `finally` clears inflight.
 *   3. Pass the response through `isPermanentAuthFailureWithRetry`.
 *      The retry-fetch is signal-less (a real second attempt), uses
 *      the same fetchFn for test parity. C5 retry-delay default
 *      (1000 ms) preserved — pre-fix sites all used the same.
 *   4. On `permanent`: dispatch `${context}-${status}` once (module-
 *      level idempotent guard in dispatchAuthFailure suppresses
 *      repeats from concurrent ingresses). Return `auth-failed`.
 *   5. On non-permanent: prefer `verdict.freshResp` if the retry
 *      succeeded (no double-fetch), parse JSON, return `ok`.
 *      Anything that throws here (non-OK status, JSON parse error)
 *      maps to `network-blip`.
 *
 * The four sites pre-deepening manually replicated all five steps,
 * with the dispatch-inside-catch pitfall surfacing twice (v0.79.18
 * Task 9, retro-audit 2026-04-30 prevkey-recovery). With the wrapper,
 * the structural error is impossible: dispatch happens INSIDE
 * fetchPlayerState, BEFORE the wrapper's try/catch ever sees it.
 */
export async function fetchPlayerState(
  opts: FetchPlayerStateOptions,
): Promise<PlayerStateFetchResult> {
  const fetcher = opts.fetchFn ?? fetch;
  const url =
    `${API_BASE}/api/games/${encodeURIComponent(opts.gameCode)}/player-state` +
    `?playerId=${encodeURIComponent(opts.playerId)}` +
    `&token=${encodeURIComponent(opts.playerToken)}`;

  let resp: Response;
  try {
    resp = await fetcher(url, opts.signal ? { signal: opts.signal } : undefined);
  } catch {
    // Network offline, AbortError from gate-timeout, DNS hiccup, etc.
    // All treated as transient — the next reconcile tick retries.
    return { kind: 'network-blip' };
  }

  const verdict = await isPermanentAuthFailureWithRetry(resp, () => fetcher(url));
  if (verdict.permanent) {
    dispatchAuthFailure(`${opts.context}-${resp.status}`);
    return { kind: 'auth-failed', status: resp.status };
  }

  const respToUse = verdict.freshResp ?? resp;
  if (!respToUse.ok) return { kind: 'network-blip' };

  try {
    const data = (await respToUse.json()) as PlayerStateResponse;
    return { kind: 'ok', data };
  } catch {
    return { kind: 'network-blip' };
  }
}
